import style from 'booking-creator/components/ManagePreAuth/style.module.css';
import { OutlinedInput, TextField, Typography } from '@material-ui/core';
import { NumericFormat } from 'react-number-format';
import { IntlService } from 'external-services/intl-service';
import { handleUpdatePreAuth, paymentActions } from 'booking-creator/reducers/payment/slice';
import ActionButton from 'design-v2/common/ActionButton';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { DiaryRootState } from 'appRoot/rootReducer';
import { BookingCreatorState } from 'booking-creator/rootReducer';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';

export interface PreAuthDialogProps {
  onOpenCloseDialog: () => void;
}
export const PreAuthDialog = (props: PreAuthDialogProps) => {
  const { onOpenCloseDialog } = props;
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  //prettier-ignore
  const staffEditRequired = useSelector((state: DiaryRootState) => state.validationReducer.staffEditBooking?.isMandatory);
  //prettier-ignore
  const preAuthErrorMsg = useSelector((state: BookingCreatorState) => state.paymentReducer.preAuthErrorMsg);
  //prettier-ignore
  const preAuthState = useSelector((state: BookingCreatorState) => state.paymentReducer.preAuthState);
  //prettier-ignore
  const { fullStatusOptions } = useSelector((state: DiaryRootState) => state.statusReducer);
  //prettier-ignore
  const activeBookingStartTime = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.time);
  //prettier-ignore
  const activeBookingEndTime = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.bookingEnd);
  //prettier-ignore
  const activeBookingStatus = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.status);
  //prettier-ignore
  const paymentSummary = useSelector((state: BookingCreatorState) => state.displayReducer.activeBooking?.paymentSummary);
  //prettier-ignore
  const staffEditEnabled = useSelector((state: DiaryRootState) => state.settingReducer.generalSettings?.staffEditEnabled);
  // prettier-ignore
  const isWideVersion = useSelector((state: BookingCreatorState) => state.bookingCreatorSettingReducer.isWideVersion);
  const currency = useSelector((state: DiaryRootState) => state.venueReducer.currency);

  const [staffName, setStaffName] = useState();
  const [staffNameError, setStaffNameError] = useState('');
  const [amountToCharge, setAmountToCharge] = useState<string>('');


  const onChangeStaffName = useCallback((v) => {
    if (!isEmpty(v.target.value)) {
      setStaffNameError('');
    }

    setStaffName(v.target.value);
  }, []);

  const onRelease = useCallback(() => {
    if (staffEditRequired && isEmpty(staffName)) {
      setStaffNameError('Staff is required');
    } else {
      dispatch(handleUpdatePreAuth({ amount: 0, isRelease: true }));
    }
  }, [amountToCharge, staffName, staffEditRequired]);

  const onCharge = useCallback(() => {
    if (staffEditRequired && isEmpty(staffName)) {
      setStaffNameError('Staff is required');
    } else {
      dispatch(handleUpdatePreAuth({ amount: Number(amountToCharge), isRelease: false }));
    }
  }, [amountToCharge, staffName, staffEditRequired]);


  useEffect(() => {
    if (!isEmpty(paymentSummary?.amountHeld)) {
      setAmountToCharge(paymentSummary?.amountHeld);
    }
  }, [paymentSummary?.amountHeld]);

  return (
    <div>
      <div className={style.header}>
        <Typography className={ isWideVersion ? style.title : style.titleForDm}>Manage Pre Authorisation</Typography>
        <CloseIcon className={style.closeIcon} onClick={onOpenCloseDialog} />
      </div>

      <div className={style.pannelRootPopup}>
        <div className={style.preAuthContent}>
          <Typography className={style.leftColumn}>Type:</Typography>
          <Typography className={style.rightDetails}>{paymentSummary?.paymentType}</Typography>
        </div>
        <div className={classNames(({
          [style.preAuthContent]: true,
          [style.extraMargin]: true
        }))}>
          <Typography className={style.leftColumn}>Status:</Typography>
          <Typography className={style.rightDetails}>{paymentSummary?.preAuthStatus}</Typography>
        </div>
        <div className={classNames(({
          [style.preAuthContent]: true,
          [style.extraMargin]: true
        }))}>
          <Typography className={style.leftColumn}>Amount held:</Typography>
          <Typography className={style.rightDetails}>{IntlService.currencyValueAsString(Number(paymentSummary?.amountHeld), currency)}</Typography>
        </div>
        <div className={classNames(({
          [style.preAuthContent]: true,
          [style.extraMargin]: true,
          [style.leftColumnExtraSpace]: true
        }))}>
          <Typography className={style.leftColumn}>Amount to charge:</Typography>
          <div>
            <NumericFormat
              value={amountToCharge}
              decimalScale={2}
              fixedDecimalScale
              allowNegative={false}
              thousandSeparator
              prefix={IntlService.getCurrencySymbol(currency).symbol}
              customInput={OutlinedInput}
              disabled={preAuthState === 'charged' || preAuthState === 'released'}
              className={style.input}
              inputProps={{
                style: {
                  padding: '8px 12px',
                },
              }}
              onValueChange={(values) => {
                const { value } = values;
                setAmountToCharge(value);

                if (Number(value || 0) > Number(paymentSummary?.amountHeld)) {
                  dispatch(paymentActions.setPreAuthState('error'));
                  dispatch(
                    paymentActions.setPreAuthErrorMsg(
                      `Maximum amount is ${IntlService.currencyValueAsString(Number(paymentSummary?.amountHeld), currency)}`
                    )
                  );
                } else if (Number(value || 0) < 1) {
                  dispatch(paymentActions.setPreAuthState('error'));
                  dispatch(
                    paymentActions.setPreAuthErrorMsg(
                      `Minimum amount is $1.00`
                    )
                  );
                } else {
                  dispatch(paymentActions.setPreAuthState('view'));
                  dispatch(paymentActions.setPreAuthErrorMsg(''));
                }
              }}
            />
          </div>
        </div>

        {preAuthState === 'error' && (
          <div className={style.errorSuccessContainer}>
            <Typography className={style.error}>{preAuthErrorMsg}</Typography>
          </div>
        )}
        {preAuthState === 'charged' && (
          <div className={style.errorSuccessContainer}>
            <Typography className={style.success}>
              {IntlService.currencyValueAsString(Number(amountToCharge), currency)} has been charged successfully
            </Typography>
          </div>
        )}
        {preAuthState === 'released' && (
          <div className={style.errorSuccessContainer}>
            <Typography className={style.success}>This pre-auth has been released</Typography>
          </div>
        )}
        <div className={style.preAuthInfo}>
          <Typography style={{fontSize: '16px'}}>
            Charge the customer's card OR release the booking guarantee.
          </Typography>
          <Typography style={{fontSize: '16px'}}>
            To make no change close this window.
          </Typography>
        </div>
        {staffEditEnabled && (
          <div className={style.rowInputContainer}>
            <TextField
              InputProps={{
                classes: {
                  input: style.input,
                },
              }}
              error={staffNameError.length > 0}
              helperText={staffNameError}
              fullWidth
              placeholder="STAFF NAME"
              value={staffName}
              onChange={onChangeStaffName}
              disabled={preAuthState === 'charged' || preAuthState === 'released'}
            />
          </div>
        )}
        {preAuthState !== 'released' && preAuthState !== 'charged' && (
          <div className={style.rowBtnGroup}>
            <ActionButton
              loading={preAuthState === 'loading'}
              fullWidth={false}
              variant={'secondary'}
              size={'regular'}
              text={ isWideVersion ? "Charge customer" : "Charge"}
              handleClick={onCharge}
              useGreyBolderColor={true}
              useBlackFontColor={true}
            />
            <div className={style.buttonContainer}>
              <ActionButton
                loading={preAuthState === 'loading'}
                fullWidth={false}
                variant={'secondary'}
                size={'regular'}
                text={ "Release"}
                handleClick={onRelease}
                useGreyBolderColor={true}
                useBlackFontColor={true}
              />
            </div>
            <ActionButton
              loading={preAuthState === 'loading'}
              variant={'primary'}
              size={'regular'}
              text="Close"
              handleClick={onOpenCloseDialog}
            />

          </div>
        )}
      </div>
    </div>
  );
};
